import { trackCreativeAction, trackLearningAction } from 'analytics';
import forge from 'apis/forge';
import SidebarToggle from 'components/Widget/Button/SidebarToggle';
import LoadingOverlay from 'components/Widget/Overlay/LoadingOverlay';
import { CreateSceneFromPromptResponse, CreateSceneResponse } from 'dragonmouse-shared/api/forge';
import { Attachment } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { showContextMenu } from 'redux/mode/actions';
import * as routes from 'routes';
import ProjectCreatorInput from './ProjectCreatorInput';
import * as S from './styles';

const ProjectCreator = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	// ProjectCreator has its own loader. Like the projects page, it does some
	// things without optimistic updates. When it does this, it shows a semi-transparent loading
	// which is different from the main App's loading indicator (which is opaque).
	// [perhaps we should generalize the main app's loader - ss]
	//
	// We use the existence of a loading message to gate the display of the loader
	const [loadingMessage, setLoadingMessage] = useState<string>('');

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onNewProject = async (prompt: string, attachments: Attachment[]) => {
		try {
			setLoadingMessage('creating project...');
			trackLearningAction('onboarding_prompt_input', 'complete');
			// Note: createSceneFromPrompt API doesn't currently support attachments or frameIds
			// These are collected for potential future use or can be added to the first chat after creation
			const res: CreateSceneFromPromptResponse = await forge.createSceneFromPrompt.withToast()({
				prompt,
				attachments,
			});
			// dispatch(setLearningCenterState({ showOnboarding: true, autoplay: true }));
			setLoadingMessage('');
			navigate(routes.scene(res.scene.id, res.scene.title));
		} catch (e) {
			setLoadingMessage('');
		}
	};

	const onCreateEmptyProject = async () => {
		try {
			setLoadingMessage('creating project...');
			const res: CreateSceneResponse = await forge.createScene.withToast()({
				title: 'My Project',
			});
			setLoadingMessage('');
			navigate(routes.scene(res.sceneId, 'My Project'));
		} catch (e) {
			setLoadingMessage('');
		}
	};

	const onCreateEmptyProjectInNewTab = async () => {
		try {
			setLoadingMessage('creating project...');
			const res: CreateSceneResponse = await forge.createScene.withToast()({
				title: 'My Project',
			});
			setLoadingMessage('');
			window.open(routes.scene(res.sceneId, 'My Project'), '_blank', 'noopener,noreferrer');
		} catch (e) {
			setLoadingMessage('');
		}
	};

	const onBackgroundContextMenu = (e: React.MouseEvent) => {
		// Only show context menu if clicking on the background, not on a project tile
		if (e.target === e.currentTarget) {
			const backgroundMenuItems = [
				new MenuItemAction('Create Empty Project', '', onCreateEmptyProject),
				new MenuItemAction('Create Empty Project in New Tab', '', onCreateEmptyProjectInNewTab),
			];
			dispatch(
				showContextMenu({
					point: { x: e.clientX + 5, y: e.clientY },
					menuItems: backgroundMenuItems,
				})
			);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<S.Base>
			<S.SidebarToggle>
				<SidebarToggle />
			</S.SidebarToggle>
			{loadingMessage && <LoadingOverlay text={loadingMessage} semiTransparent />}
			<S.Content onContextMenu={onBackgroundContextMenu}>
				<S.Input>
					<S.Title>What would you like to design&nbsp;today?</S.Title>
					<ProjectCreatorInput onSubmit={onNewProject} disabled={!!loadingMessage} />
				</S.Input>
			</S.Content>
		</S.Base>
	);
};

export default ProjectCreator;
